import { PropsWithChildren, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { maskDecimal } from '@givebutter/util';
import { faBuilding, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faClipboardCheck, faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QuickBooksProcessMode, rebuild } from 'api/quickBooks';
import {
    QUICKBOOKS_SYNC_STATUS_MAP,
    QuickBooksTransactionSyncStatus,
    READY_TO_SYNC_FILTER,
} from 'assets/js/pages/account-settings/account-integrations/QuickBooks/constants';
import { useQuickBooksDetails } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksDetails';
import { useQuickBooksSyncRefresh } from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksSyncRefresh';
import { getComputedContactProps } from 'assets/js/pages/contacts/transform';
import { TransactionRecurringPlanBadge } from 'assets/js/pages/transactions/TransactionRecurringPlanBadge';
import { TransactionsTableProvider } from 'assets/js/pages/transactions/TransactionsTableProvider';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/transactions/util';
import { FilterBarContainer } from 'components/filter/filters-next/FilterBarContainer';
import { FilterGroupBar } from 'components/filter/filters-next/FilterGroupBar';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useInitFilters } from 'components/filter/filters-next/useInitFilters';
import * as Table from 'components/table';
import { TableContext } from 'components/table/TableContext';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer, rawDateFromServer } from 'helpers/dates';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { twMerge } from 'tailwind-merge';

const QuickBooksTransactionsTable = (props: PropsWithChildren) => {
    const { children } = props;
    const { timezone } = useCurrentAccount();
    const { accountId } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const { filters } = useFiltersByKey('quickBooksTransactions');
    const queryClient = useQueryClient();

    const { isFetching: isRebuilding, isSuccess } = useQuery({
        queryKey: accountQueryKeys.quickBooksRebuild(accountId),
        queryFn: () => rebuild(accountId),
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        staleTime: 0,
    });

    useEffect(() => {
        if (isSuccess) {
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
        }
    }, [accountId, isSuccess, queryClient]);

    useQuickBooksSyncRefresh();

    return (
        <TableCoreResourceProvider
            filterCacheKey="quickBooksTransactions"
            resource={'transactions'}
            initialState={{
                sortingState: [{ id: 'transacted_at', desc: true }],
            }}
            columns={columnHelper => [
                columnHelper.accessor('transacted_at', {
                    header: () => (
                        <div className={'flex gap-1'}>
                            <p>Date</p>
                            <p className={'text-xs leading-5 text-gray-400'}>
                                ({DateTime.now().setZone(timezone).offsetNameShort})
                            </p>
                        </div>
                    ),
                    cell: info =>
                        rawDateFromServer(info.getValue())
                            .setZone(timezone)
                            .toLocaleString(DateTime.DATE_FULL),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('first_name', {
                    id: 'contact_name',
                    header: curriedTransformTableKeyToReadable,
                    enableHiding: false,
                    cell: info => {
                        const { contact } = info.row.original;

                        if (!contact) {
                            return <EmptyData />;
                        }

                        const contactProps = getComputedContactProps(contact);

                        return (
                            <Link
                                to={`/accounts/${accountId}/${contactProps.linkUrl}`}
                                className={'max-w-40 font-medium text-black'}
                            >
                                <div className="flex gap-2">
                                    <TruncatedText>{contactProps.contactName}</TruncatedText>
                                    {contactProps.isCompany && (
                                        <Badge size="sm" color="success">
                                            <FontAwesomeIcon icon={faBuilding} /> Company
                                        </Badge>
                                    )}
                                </div>
                            </Link>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
                columnHelper.accessor('integrations.quickbooks.mapping.service_item_name', {
                    header: 'Service Item',
                    enableSorting: false,
                    cell: ({ getValue }) => {
                        const value = getValue();
                        if (value) {
                            return (
                                <TruncatedText>
                                    <p className="max-w-32">{value}</p>
                                </TruncatedText>
                            );
                        }
                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('integrations.quickbooks.mapping.class_name', {
                    header: 'Class',
                    enableSorting: false,
                    cell: ({ getValue }) => {
                        const value = getValue();
                        if (value) {
                            return (
                                <TruncatedText>
                                    <p className="max-w-32">{value}</p>
                                </TruncatedText>
                            );
                        }
                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('integrations.quickbooks.mapping.account_name', {
                    header: 'Account',
                    enableSorting: false,
                    cell: ({ getValue }) => {
                        const value = getValue();
                        if (value) {
                            return (
                                <TruncatedText>
                                    <p className="max-w-32">{value}</p>
                                </TruncatedText>
                            );
                        }
                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('integrations.quickbooks.mapping.status', {
                    header: 'Sync Status',
                    enableSorting: false,
                    cell: ({ getValue }) => {
                        const value = getValue();
                        if (value) {
                            return <SyncStatus value={value} />;
                        }
                        return <Table.CellDataOrEmpty />;
                    },
                }),
                columnHelper.accessor('amount', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const transaction = info.row.original;

                        return (
                            <div className={'flex items-center gap-2'}>
                                <p>
                                    {currencySymbol}
                                    {maskDecimal(info.getValue())}
                                </p>
                                <TransactionRecurringPlanBadge transaction={transaction} />
                            </div>
                        );
                    },
                    meta: {
                        sortType: 'numeric',
                    },
                }),
            ]}
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: false,
                enableExpanding: false,
                meta: {
                    enableColumnOrder: false,
                    to: cell =>
                        `/accounts/${accountId}/settings/integrations/quickbooks/transaction/${cell.row.original.id}`,
                },
            }}
            coreTableOptions={{
                tableStateOptions: {
                    params: {
                        filters,
                        has_quickbooks: true,
                        per_page: 20,
                    },
                },
            }}
        >
            <TransactionsTableProvider>
                <Box className="flex flex-col">
                    <div className={'flex flex-col'}>
                        <QuickBooksSyncStatus />
                        {isRebuilding && (
                            <div className="p-3">
                                <p className="flex items-center gap-2 text-sm italic text-gray-500">
                                    <FontAwesomeIcon
                                        icon={faSpinnerThird}
                                        size="xs"
                                        className="animate-spin"
                                    />
                                    Checking for new transactions...
                                </p>
                            </div>
                        )}
                        <FilterBarContainer>
                            <FilterGroupBar quickFilters={['quickBooksTransactionStatus']} />
                        </FilterBarContainer>
                    </div>
                    <Table.FiltersBar />
                    <Table.Filters />
                    <LoadingOverlay isLoading={isRebuilding} delay={0} className="">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <QuickBooksTransactionsEmptyState>
                            {children}
                        </QuickBooksTransactionsEmptyState>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown />
                        </Table.Pagination>
                    </LoadingOverlay>
                </Box>
            </TransactionsTableProvider>
        </TableCoreResourceProvider>
    );
};

const QuickBooksSyncStatus = () => {
    const { data } = useQuickBooksDetails();
    const autoSync = data.settings.auto_sync;

    const showLoader =
        data.process_mode !== QuickBooksProcessMode.Idle &&
        data.process_mode !== QuickBooksProcessMode.Disconnected;

    return (
        <div className={'grid grid-cols-4 gap-3.5 border-b border-b-gray-100'}>
            <div className="flex flex-col gap-1 px-4 py-2">
                <div className="text-xs text-gray-600">Auto-sync</div>
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <span
                        className={twMerge(
                            'inline-block size-1.5 rounded-full bg-red-500',
                            autoSync && 'bg-green-500',
                        )}
                    ></span>
                    {autoSync ? 'On' : 'Off'}
                </div>
            </div>
            <div className="flex flex-col gap-1 px-4 py-2">
                <div className="text-xs text-gray-600">Last sync</div>
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                    {data.settings.last_sync ? (
                        dateFromServer(data.settings.last_sync).toLocaleString(
                            DateTime.DATETIME_SHORT,
                        )
                    ) : (
                        <EmptyData />
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-1 px-4 py-2">
                <div className="text-xs text-gray-600">Next sync</div>
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                    {autoSync && data.settings.next_sync ? (
                        dateFromServer(data.settings.next_sync).toLocaleString(
                            DateTime.DATETIME_SHORT,
                        )
                    ) : (
                        <EmptyData />
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-1 px-4 py-2">
                <div className="text-xs text-gray-600">Status</div>
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                    {showLoader && (
                        <FontAwesomeIcon size="xs" icon={faSpinnerThird} className="animate-spin" />
                    )}
                    {capitalize(data.process_mode)}
                </div>
            </div>
        </div>
    );
};

const SyncStatus = ({ value }: { value: QuickBooksTransactionSyncStatus }) => {
    const label = QUICKBOOKS_SYNC_STATUS_MAP[value];
    const color = (
        {
            ready_to_sync: 'primary',
            issues: 'error',
            do_not_sync: 'neutral',
            synced: 'success',
            syncing: 'primary',
            before_historical_date: 'neutral',
        } as const
    )[value];

    return <Badge color={color}>{label}</Badge>;
};

const QuickBooksTransactionsEmptyState = (
    props: React.ComponentProps<typeof Table.EmptyWhenNotSearching>,
) => {
    const { isFiltered } = useContext(TableContext);

    return (
        <Table.Empty variant={'primary'} className="max-w-md">
            <Table.EmptyIcon>
                <FontAwesomeIcon icon={isFiltered ? faGlobe : faClipboardCheck} />
            </Table.EmptyIcon>
            <Table.EmptyTitle>Up to date</Table.EmptyTitle>
            <Table.EmptyDescription>
                It looks like everything is working as intended.
            </Table.EmptyDescription>
            <Table.EmptyWhenNotSearching {...props} />
        </Table.Empty>
    );
};

const InitFiltersWrapper = () => {
    const { didInit } = useFiltersByKey('quickBooksTransactions');
    useInitFilters('quickBooksTransactions', transformSavedFilterGroups(READY_TO_SYNC_FILTER));

    if (didInit) {
        return <QuickBooksTransactionsTable />;
    }

    return null;
};

export { InitFiltersWrapper as QuickBooksTransactionsTable };
